import React from "react";
import * as MUI from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import SimpleAlert from "../../../../../../components/display/SimpleAlert";
import { useFetch } from "../../../../../../hooks/Fetching";
import {
  GET_WAREHOUSE_SUPPLIERS,
  WAREHOUSE_SUPPLIER,
} from "../../../../../../enums/ApiPaths";
import { WAREHOUSE_SUPPLIERS, WAREHOUSE_ITEMS } from "../../../../../../enums/QueryKeys";
import useWarehouseNamedEntityMutations from "../hooks/useWarehouseNamedEntityMutations";
import WarehouseNamedEntityTable from "./WarehouseNamedEntityTable";
import WarehouseNamedEntityFormDialog from "./WarehouseNamedEntityFormDialog";
import DeleteWarehouseNamedEntityDialog from "./DeleteWarehouseNamedEntityDialog";
import UserHint from "../../../../../../components/display/UserHint";

const WarehouseSuppliersTab = React.forwardRef(function WarehouseSuppliersTab(
  { clinicId },
  ref,
) {
  const { t } = useTranslation();
  const queryKey = [WAREHOUSE_SUPPLIERS, clinicId];
  const { data, isLoading, error } = useFetch(GET_WAREHOUSE_SUPPLIERS + clinicId, null, queryKey, Boolean(clinicId));
  const suppliers = React.useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const { create, isCreating, update, isUpdating, remove, isDeleting } = useWarehouseNamedEntityMutations({
    createUrl: GET_WAREHOUSE_SUPPLIERS + clinicId,
    entityBaseUrl: WAREHOUSE_SUPPLIER,
    queryKey,
    invalidateOnDelete: [WAREHOUSE_ITEMS],
  });

  // Persisted to the URL (not local state) so the search is preserved on
  // refresh/back-navigation and the filtered view is shareable - matches
  // WarehouseItemsTable.jsx's existing pattern. Own param name (not the
  // Items tab's shared WAREHOUSE_TABLE_PARAMS.search) so switching tabs
  // doesn't leak one tab's search text into another's search box.
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get("supplierSearch") || "";
  function setSearch(value) {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (value) {
        params.set("supplierSearch", value);
      } else {
        params.delete("supplierSearch");
      }
      return params;
    });
  }
  const [formState, setFormState] = React.useState(null); // { entity: null | entity }
  const [entityPendingDelete, setEntityPendingDelete] = React.useState(null);

  // No backend search for this catalog-sized list (clinic-scoped
  // suppliers, not warehouse-item-scale) - filtering the already-fetched
  // array client-side is proportionate rather than adding a new endpoint.
  const filteredSuppliers = React.useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return suppliers;
    return suppliers.filter((supplier) =>
      supplier.name?.toLowerCase().includes(query),
    );
  }, [suppliers, search]);

  React.useImperativeHandle(ref, () => ({
    openAddDialog: () => setFormState({ entity: null }),
  }));

  async function handleSubmit(data) {
    const success = formState.entity
      ? await update(formState.entity.id, data)
      : await create(data);
    if (success) {
      setFormState(null);
    }
  }

  async function handleDelete(entity) {
    const success = await remove(entity.id);
    if (success) {
      setEntityPendingDelete(null);
    }
  }

  if (error) {
    return <SimpleAlert message="ErrorFetchingWarehouseItemsData" severity="error" />;
  }

  return (
    <MUI.Stack spacing={3}>
      <MUI.Paper variant="outlined" sx={{ p: 2, borderRadius: 3, borderColor: "divider" }}>
        <MUI.TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("SearchWarehouseSuppliers")}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <MUI.InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </MUI.InputAdornment>
            ),
          }}
          inputProps={{ "data-testid": "warehouse.suppliers.search-input" }}
        />
      </MUI.Paper>
      <UserHint hint={"DisabledItemsAreInUse"}/>
      <WarehouseNamedEntityTable
        items={filteredSuppliers}
        isLoading={isLoading}
        emptyMessage="NoWarehouseSuppliersFound"
        onEdit={(entity) => setFormState({ entity })}
        onDelete={(entity) => setEntityPendingDelete(entity)}
        testIdPrefix="warehouse.suppliers"
      />

      {formState && (
        <WarehouseNamedEntityFormDialog
          title={formState.entity ? t("EditWarehouseSupplier") : t("AddWarehouseSupplier")}
          initialValues={formState.entity}
          isSubmitting={isCreating || isUpdating}
          onSubmit={handleSubmit}
          onClose={() => setFormState(null)}
          testId="warehouse.suppliers.form-dialog"
        />
      )}

      <DeleteWarehouseNamedEntityDialog
        testId="warehouse.suppliers.delete-dialog"
        entity={entityPendingDelete}
        title={t("DeleteWarehouseSupplier")}
        confirmMessageKey="ReallyWantDeleteWarehouseSupplier"
        isDeleting={isDeleting}
        onCancel={() => setEntityPendingDelete(null)}
        onConfirm={handleDelete}
      />
    </MUI.Stack>
  );
});

export default WarehouseSuppliersTab;
